import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionSummary, AccordionDetails, Box, Container, Typography, Divider, Alert,
        Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, useMediaQuery } from "@mui/material";
import { Close} from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPuzzlePiece, faShapes, faCircleCheck, faCoffee, faGlobe, faProjectDiagram, faBuilding } from "@fortawesome/free-solid-svg-icons";

import { useSearchDetailQuery } from "../../hooks/useSearch/useSearchDetailQuery";
import { useSurveyCoffeeKeyQuery } from "../../hooks/useSurvey/useSurveyCoffeeKey";
import { processingTypes } from "../../constants/searchpageTexts";

import ContentLayout from "../../layouts/ContentLayout";
import { Item, CardItem } from "../board/ControlUtils";
import { ChartIntensity, ChartWordCloud, ChartDescriptiveSection1, ChartDescriptiveSection2, ChartAffective, ChartRadar, ChartRadar2, ChartScatter } from "./";
import { ControlCircularProgressBar, } from "../../components/board";
import { isDebug, utils } from "../../utils";
import PropTypes from "prop-types";


export function MakeInfoBox ({TotalScore, Country, Company, Processing, FontSize}) {
    const { t } = useTranslation();

    return (
        <>
            <Divider sx={{ my: 1, borderStyle: "dotted" }} />
            <Box sx={{ p: 0, m: 0, width: "inherit", height: "100%", borderRadius: 1.5, 
                        bgcolor: '#F5F5F5', '&:hover': { bgcolor: '#F1F1F1', color: 'white', }, }}>

                <Grid container alignItems="center" spacing={0.5} sx={{  '--Grid-borderWidth': '1px',
                                                                                    borderColor: 'divider',
                                                                                    '& > div.divline': {
                                                                                        borderTop: 'var(--Grid-borderWidth) dotted',
                                                                                        borderColor: 'divider',
                                                                                        lineHeight: '3px',
                                                                                        height: '3px',
                                                                                    },
                }}>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }} sx={{ p: 0, m: 0 }} >
                        <Item sx={{ textAlign: "left", fontSize: FontSize }}> 
                            <FontAwesomeIcon icon={faGlobe} size="lg" style={{ paddingLeft: '10px', paddingRight: '10px', color: '#03124f' }} />
                            {t('assessment.country')} : {Country}
                        </Item>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }} sx={{ p: 0, m: 0 }} >
                        <Item sx={{ textAlign: "left", fontSize: FontSize }}> 
                            <FontAwesomeIcon icon={faProjectDiagram} size="lg" style={{ paddingLeft: '10px', paddingRight: '10px', color: '#03124f' }} />
                            {t('assessment.processing')} : {Processing}
                        </Item>
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }} sx={{ p: 0, m: 0 }} >
                        <Item sx={{ textAlign: "left", fontSize: FontSize }}> 
                            <FontAwesomeIcon icon={faBuilding} size="lg" style={{ paddingLeft: '10px', paddingRight: '10px', color: '#03124f' }} />
                            {t('field.companyname')} : {Company}
                        </Item>
                    </Grid>
                    {TotalScore > 0 ?
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }} sx={{ p: 0, m: 0 }} >
                        <Item sx={{ textAlign: "left" }}> 
                            <FontAwesomeIcon icon={faShapes} size="lg" style={{ paddingLeft: '10px', paddingRight: '40px', color: '#03124f' }} />
                            <span style={{ fontSize: `calc(${FontSize} + 4px)`, fontWeight: "900", color: '#03124f' }}>{TotalScore}</span> 
                            <span style={{ fontSize: FontSize }}> / 100</span> 
                        </Item>
                    </Grid>
                    : null }
                </Grid>
            </Box>
            <Divider sx={{ my: 1, borderStyle: "dotted" }} />
        </>
    );
};
MakeInfoBox.propTypes = {
    TotalScore: PropTypes.number.isRequired,
    Country: PropTypes.string.isRequired, 
    Company: PropTypes.string.isRequired, 
    Processing: PropTypes.string.isRequired,
    FontSize: PropTypes.string.isRequired,
};
export function MakeDescriptiveChart({itemValue}) {
    const { t } = useTranslation();

    return (
        <>
            {/* Descriptive Section1 & Section2 Chart */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', p: 0, m: 0}}>
                <Grid container alignItems="center" spacing={1} >

                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ justifyItems: "center" }} >

                        <CardItem title={`${t('assessment.fragrance')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.fraIns ? 
                            <ChartIntensity chartName={t('assessment.fragrance')} jsonData={itemValue?.desc?.fraIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem title={`${t('assessment.aroma')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.aromaIns ? 
                            <ChartIntensity chartName={t('assessment.aroma')} jsonData={itemValue?.desc?.aromaIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem>
                            {itemValue?.desc?.section1 ?
                            <ChartDescriptiveSection1 jsonData={itemValue?.desc?.section1} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                    </Grid>

                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ justifyItems: "center" }} >

                        <CardItem title={`${t('assessment.flavor')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.flaIns ?
                            <ChartIntensity chartName={t('assessment.flavor')} jsonData={itemValue?.desc?.flaIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem title={`${t('assessment.aftertaste')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.aftIns ?
                            <ChartIntensity chartName={t('assessment.aftertaste')} jsonData={itemValue?.desc?.aftIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem>
                            {itemValue?.desc?.section2 ? 
                            <ChartDescriptiveSection2 jsonData={itemValue?.desc?.section2} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                    </Grid>

                </Grid>
            </Container>
            <Divider sx={{ my: 1, borderStyle: "dotted" }} />

            {/* Acidity, Sweetness, Mouthfeel / Main Tastes, Mouthfeel*/}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', p: 0, m: 0}}>
                <Grid container alignItems="center" spacing={1} sx={{ p: 0, m: 0, width: "100%", }} >

                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ width: "100%", justifyItems: "center" }} >

                        <CardItem title={`${t('assessment.acidity')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {(itemValue?.desc?.acdIns) ?
                            <ChartIntensity chartName={t('assessment.acidity')} jsonData={itemValue?.desc?.acdIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem title={`${t('assessment.sweetness')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.swtIns ? 
                            <ChartIntensity chartName={t('assessment.sweetness')} jsonData={itemValue?.desc?.swtIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem title={`${t('assessment.mouthfeel')} ${t('assessment.intensity')}`} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.mfIns ?
                            <ChartIntensity chartName={t('assessment.mouthfeel')} jsonData={itemValue?.desc?.mfIns} />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>
                    
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ justifyItems: "center", width: "100%" }} >
                        <CardItem title={t('assessment.maintastes')} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.mainTastes ?
                            <ChartWordCloud chartName={t('assessment.maintastes')} jsonData={itemValue?.desc?.mainTastes} chartHeight="220px" />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>

                        <CardItem title={t('assessment.mouthfeel')} iconFontAwesome={faCoffee} >
                            {itemValue?.desc?.mouthfeel ?
                            <ChartWordCloud chartName={t('assessment.mouthfeel')} jsonData={itemValue?.desc?.mouthfeel} chartHeight="220px" />
                            : <Alert severity="info">{t('message.loading')}</Alert>}
                        </CardItem>
                    </Grid>

                </Grid>
            </Container>
        </>
    );
};
MakeDescriptiveChart.propTypes = {
    itemValue: PropTypes.object.isRequired,
};
export function MakeAffectiveChart({itemValue, FontSize}) {
    const { t } = useTranslation();
    return (
        <>
            {/* Affective Assessment Data */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', alignItems: 'center', justifyItems: "center", p: 0, m: 0, boxShadow: 'none',}}>
                <Accordion key='Chart#1' sx={{ width: '100%', mb: 1, boxShadow: 'none', }}>
                    <AccordionSummary expandIcon={<DocumentScannerIcon />} aria-controls="panel1a-content" id="panel1a-header" >
                        <Typography component='h6' variant="h6" >
                            Data
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>

                        <Box sx={{ width: '100%', display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))',
                                gap: 2, }}
                        >
                            {itemValue?.aff?.section.sort((a, b) => { 
                                        const aValue = a.value || 0;
                                        const bValue = b.value || 0;
                                        if (aValue > bValue) return -1; 
                                        if (aValue === bValue) return 0; 
                                        return 1;
                            }).map((vlv, index) => (
                                <CardItem key={`${index}_${vlv.name}`} sx={{ lineHeight: "21px", height: "21px" }}>
                                    <div style={{ display: "inline-block", width: "100%" }}> 
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span style={{ fontWeight: "bold", fontSize: FontSize, color: vlv.itemStyle.color }}>{t(`assessment.${vlv.name.toLowerCase().trim().replace(' ', '')}`)}</span>
                                            <span style={{ fontWeight: "bold", fontSize: FontSize, color: vlv.itemStyle.color }}>{vlv.value}</span>
                                        </div>
                                    </div>
                                </CardItem>
                            ))}
                        </Box>

                    </AccordionDetails>
                </Accordion>
            </Container>

            {/* Affective Assessment Chart */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', alignItems: 'center', justifyItems: "center", p: 0, m: 0}}>
                {(itemValue?.aff?.section) ?
                <ChartAffective jsonData={itemValue?.aff?.section} />
                : <Alert severity="info">{t('message.loading')}</Alert>}
            </Container>

            <Divider sx={{ my: 1, borderStyle: "dotted" }} />
            
            {/* Defect Chart */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', alignItems: 'center', justifyItems: "center", p: 0, m: 0}}>
                <CardItem title={t('assessment.defect')} iconFontAwesome={faCoffee} >
                    {(itemValue?.aff?.defect) ? 
                    <ChartWordCloud chartName={t('assessment.defect')} jsonData={itemValue?.aff?.defect} chartHeight="150px" />
                    : <Alert severity="info">{t('message.loading')}</Alert>}
                </CardItem>
                
            </Container>
        </>
    );
};
MakeAffectiveChart.propTypes = {
    itemValue: PropTypes.object.isRequired,
    FontSize: PropTypes.string.isRequired,
};

export function MakeChartScatter({itemValue}) {
    const { t } = useTranslation();

    return (
        <>
            {/* OverView ChartScatter Chart */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', p: 0, m: 0}}>
                <Grid container alignItems="center" spacing={1} sx={{ p: 0, m: 0, width: "100%", }} >
                    <Grid item size={{ xs: 12, sm: 12, md: 12 }} sx={{ width: "100%", justifyItems: "center" }} >
                        {(itemValue?.aff?.section && itemValue?.desc) ?
                        <ChartScatter chartName='Taste OverView' jsonDataX={itemValue?.aff?.section} jsonDataY={itemValue?.desc} chartHeight='400px' />
                        : <Alert severity="info">{t('message.loading')}</Alert>}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};
MakeChartScatter.propTypes = {
    itemValue: PropTypes.object.isRequired,
};

export function MakeMixedChart({itemValue}) {
    const { t } = useTranslation();

    return (
        <>
            {/* Radar Chart */}
            <Container className="chart-content" sx={{ width: '100%', height: '100%', p: 0, m: 0}}>
                <Grid container alignItems="center" spacing={1} sx={{ p: 0, m: 0, width: "100%", }} >
                    {/* Descriptive Radar Chart */}
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ width: "100%", justifyItems: "center" }} >
                        {(itemValue?.desc) ?
                        <ChartRadar chartName='Taste Intensity' jsonData={itemValue?.desc} chartHeight='400px' />
                        : <Alert severity="info">{t('message.loading')}</Alert>}
                    </Grid>

                    {/* Affective Radar Chart */}        
                    <Grid item size={{ xs: 12, sm: 12, md: 6 }} sx={{ width: "100%", justifyItems: "center" }} >
                        {(itemValue?.aff?.section) ?
                        <ChartRadar2 chartName='Affective Assessment' jsonData={itemValue?.aff?.section} chartHeight='400px' />
                        : <Alert severity="info">{t('message.loading')}</Alert>}
                    </Grid>
                </Grid>
            </Container>
        </>
    );
};
MakeMixedChart.propTypes = {
    itemValue: PropTypes.object.isRequired,
};

export function MakeDebugShowData ({JsonData}) {
    const [open, setOpen] = React.useState(false);

    const onClose = () => {
        setOpen(false); 
    };

    return (
        <>
            {/* 디버깅 영역 */}
            <Button variant="contained" color="primary" onClick={() => { setOpen(true); }} sx={{ mt: 2, mb: 2 }}>
                <DocumentScannerIcon sx={{ mr: 1 }} />
                Show DataSet
            </Button>
            <Dialog open={open} onClose={onClose} scroll="paper" aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description" maxWidth="lg">
                <DialogTitle id="scroll-dialog-title">SCA Coffee Flavor Wheel DataSet</DialogTitle>
                <Box position="absolute" top={0} right={0}>
                    <IconButton onClick={() => onClose()}>
                        <Close />
                    </IconButton>
                </Box>
                <DialogContent dividers={true} sx={{ padding: '1px' }}>
                    <DialogContentText id="scroll-dialog-description" tabIndex={-1}>
                        <div style={{ padding: '0px', minWidth: '500px', maxWidth: '1280px', maxHeight: '700px', overflowY: 'auto', fontSize: '8px', whiteSpace: 'pre-wrap', backgroundColor: '#131213', color: '#fff' }}>
                            <button onClick={() => { utils.etc.copyToClipboard(JSON.stringify(JsonData, null, 2)); }} style={{ marginBottom: '5px' }}> copy to clipboard </button>
                            <code>
                            {JSON.stringify(JsonData, null, 2)?.split('\n').map((item, idx) => {
                                return (
                                    <Typography component='p' key={`${idx}${item}`}>&nbsp; {item}</Typography>
                                )
                            })}
                            </code>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
MakeDebugShowData.propTypes = {
    JsonData: PropTypes.object.isRequired,
};

export function EvaluationLoad () {
    const { coffee_key_id } = useParams();
    const { data: details } = useSearchDetailQuery(coffee_key_id);
    const { data: jsonData } = useSurveyCoffeeKeyQuery(coffee_key_id);

    return (
        <>
        {details && jsonData ?
            <EvaluationResults JsonData={jsonData?.data} CoffeeName={details?.CoffeeName} Country={details?.CountryNameEng} Company={details?.Company} Processing={processingTypes[details?.ProcessingCode]} />
            : null
        }
        </>
    )
}


export default function EvaluationResults ({JsonData, CoffeeName, Country, Company, Processing}) {
    const { t, i18n } = useTranslation();
    const isxsMobile = useMediaQuery('(max-width:320px)');
    const issMobile = useMediaQuery('(max-width:410px)');
    const isMobile = useMediaQuery('(max-width:600px)');
    const isSm = useMediaQuery('(max-width:900px)');
    const isMd = useMediaQuery('(max-width:1200px)');

    const getPadding = () => {
        if (isxsMobile) return "3px";
        if (issMobile) return "5px";
        if (isMobile) return "7px";
        if (isSm) return "11px";
        if (isMd) return "25px";
        return "25px"; // Default padding for larger screens
    }

    const getFontSize = () => {
        if (isxsMobile) return "12px";
        if (issMobile) return "14px";
        if (isMobile) return "16px";
        if (isSm) return "18";
        if (isMd) return "20px";
        return "20px"; // Default padding for larger screens
    }
    const showTimerControler = React.useRef(null);

    return (
        <ContentLayout pageTitle={"SCA Coffee Value Assessment"} pageDescription={null} pd='0px' addClassName="content-layout-sub3">

            <Typography component='h3' variant="h3" sx={{ mt: 2, fontWeight: "500", textAlign: "center", alignItems: "center" }}>
                <FontAwesomeIcon icon={faCircleCheck} style={{ paddingLeft: '0px', paddingRight: '10px' }} />
                {CoffeeName}
            </Typography>

            <Container sx={{ width: "100%", height: "100%", p: 0 }} > 

                <MakeInfoBox TotalScore={JsonData?.aff?.totalScr || 0} Country={Country} Company={Company} Processing={Processing} FontSize={getFontSize()} />

                {JsonData?.desc !== null && JsonData?.aff !== null ?
                <MakeChartScatter itemValue={JsonData} />
                : null}
               
                {JsonData?.desc !== null ?
                <>
                    <Typography component='h4' variant="h4" sx={{ p: 0, m: 0, mt: 4, }}>
                        <FontAwesomeIcon icon={faPuzzlePiece} style={{ paddingLeft: '10px', paddingRight: '10px' }} />
                        {t('assessment.descriptiveassessment')} {i18n.language.toLowerCase() === 'ko' ? '(Descriptive Assessment)' : null}    
                    </Typography>
                    <Divider sx={{ my: 1, borderStyle: "dotted" }} />
                    <MakeDescriptiveChart itemValue={JsonData} />

                    {/* Split Space */}
                    <div style={{ display: "inline-block", width: "100%", height: "10px", lineHeight: "10px", padding: "10x", marginTop: "10px" }}></div>
                </>
                : null}

                {JsonData?.aff !== null ?
                <>
                    <Typography component='h4' variant="h4" sx={{ p: 0, m: 0}}>
                        <FontAwesomeIcon icon={faPuzzlePiece} style={{ paddingLeft: '10px', paddingRight: '10px' }} />
                        {t('assessment.affective')} {t('assessment.assessment')} {i18n.language.toLowerCase() === 'ko' ? '(Affective Assessment)' : null}
                    </Typography>
                    <Divider sx={{ my: 1, borderStyle: "dotted" }} />
                    <MakeAffectiveChart itemValue={JsonData} FontSize={getFontSize()} />

                    {/* Split Space */}
                    <div style={{ display: "inline-block", width: "100%", height: "10px", lineHeight: "10px", padding: "10x", marginTop: "10px" }}></div>
                </>
                : null}
               
                {JsonData?.desc !== null && JsonData?.aff !== null ?
                <>
                    <Typography component='h4' variant="h4" sx={{ p: 0, m: 0, mt: 4, }}>
                        <FontAwesomeIcon icon={faPuzzlePiece} style={{ paddingLeft: '10px', paddingRight: '10px' }} />
                        Mixed  
                    </Typography>
                    <Divider sx={{ my: 1, borderStyle: "dotted" }} />
                    <MakeMixedChart itemValue={JsonData} />
                </> 
                : null}
                

            </Container>
            <Divider sx={{ my: 1, borderStyle: "dotted" }} />

            {/* Circular Progress Bar */}
            <ControlCircularProgressBar ref={showTimerControler} initialTime={2} />

            {isDebug () ?
            <MakeDebugShowData JsonData={JsonData} />
            :null}

        </ContentLayout>
    );
};
EvaluationResults.propTypes = {
    JsonData: PropTypes.object.isRequired,
    CoffeeName: PropTypes.string.isRequired,
    Country: PropTypes.string.isRequired,
    Company: PropTypes.string.isRequired,
    Processing: PropTypes.string.isRequired,
};
